import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { PortalKopf, PortalSeite, datum } from '../components/portal/PortalKopf';
import { SignaturPad } from '../components/SignaturPad';

const Ergebnis = ({ z }) => (
  <div className={`p-5 rounded-2xl border ${z.status === 'zugestimmt' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-gray-200 bg-white text-[#34536b]'}`} data-testid="zustimmung-ergebnis">
    {z.status === 'widerrufen' ? 'Dieser Link wurde von der Kanzlei zurückgezogen. Es ist nichts weiter zu tun.'
      : `Ihre Entscheidung „${z.status}“ wurde am ${datum(z.entscheidung?.am)} gespeichert. Die Kanzlei hat einen Nachweis als PDF erhalten.`}
  </div>
);

const Formular = ({ z, onDone }) => {
  const [ok, setOk] = useState(false);
  const [bem, setBem] = useState('');
  const [png, setPng] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const senden = async (entscheidung) => {
    setBusy(true); setErr('');
    try { await api.post(`/portal/zustimmungen/${z.id}`, { entscheidung, bestaetigt: ok, bemerkung: bem, unterschrift: entscheidung === 'zugestimmt' ? png : null }); onDone(); } catch (e) { setErr(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <label className="flex items-start gap-3 p-4 bg-[#fbf7f0] rounded-2xl border border-[#b8893d]/40 cursor-pointer">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 w-4 h-4 accent-[#1B3A5C]" data-testid="zustimmung-bestaetigen" />
        <span className="text-sm text-[#1B3A5C]">{z.erklaerung}</span>
      </label>
      <div className="p-4 bg-white rounded-2xl border border-gray-200" data-testid="zustimmung-unterschrift">
        <p className="text-xs text-[#7a8a99] mb-2">Unterschrift (freiwillig) – mit Finger, Stift oder Maus. Sie erscheint im Nachweis-PDF.</p>
        <SignaturPad onChange={setPng} label="Hier unterschreiben" testid="zustimmung-pad" />
      </div>
      <textarea value={bem} onChange={(e) => setBem(e.target.value)} rows={2} placeholder="Bemerkung an die Kanzlei (optional)" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-[#b8893d] focus:outline-none" data-testid="zustimmung-bemerkung" />
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => senden('zugestimmt')} disabled={!ok || busy} className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#1B3A5C] text-white text-sm hover:bg-[#2A5280] disabled:opacity-40 transition-colors" data-testid="zustimmung-zustimmen">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}Verbindlich zustimmen</button>
        <button type="button" onClick={() => senden('abgelehnt')} disabled={busy} className="h-11 px-5 rounded-xl border border-gray-300 text-sm text-[#34536b] hover:border-rose-300 hover:text-rose-700 transition-colors" data-testid="zustimmung-ablehnen">Nicht zustimmen</button>
      </div>
      <p className="text-[11px] text-gray-500">Mit Ihrer Entscheidung werden Zeitpunkt, Ihr Benutzerkonto, IP-Adresse und Browser zum Nachweis gespeichert.</p>
      {err && <p className="text-sm text-rose-700" role="alert" data-testid="zustimmung-fehler">{err}</p>}
    </div>
  );
};

export default function PortalZustimmung() {
  const { zid } = useParams();
  const [z, setZ] = useState(null);
  const [err, setErr] = useState('');
  const load = () => api.get(`/portal/zustimmungen/${zid}`).then((r) => setZ(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Nicht gefunden'));
  useEffect(() => { load(); }, [zid]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <PortalSeite testId="portal-zustimmung">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] pb-28">
        <PortalKopf kicker="Ihre Zustimmung" titel={z ? `${z.art_label}${z.aktenzeichen ? ` · AZ ${z.aktenzeichen}` : ''}` : 'Zustimmung'}>
          {z && <Link to={`/portal/akten/${z.case_id}`} className="mt-3 inline-flex items-center gap-1 text-xs text-white/70 hover:text-white" data-testid="zustimmung-zur-akte"><ArrowLeft className="w-3.5 h-3.5" />Zur Akte</Link>}
        </PortalKopf>
        <div className="max-w-2xl mx-auto px-5 mt-8 space-y-5">
          {err && <p className="p-5 bg-white rounded-2xl border border-gray-200 text-sm text-rose-700" data-testid="zustimmung-nicht-gefunden">{err}</p>}
          {!z && !err && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#b8893d]" /></div>}
          {z && (<>
            <section className="p-5 bg-white rounded-2xl border border-gray-200 text-sm text-[#34536b] space-y-1" data-testid="zustimmung-angelegenheit">
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#b8893d]">Angelegenheit</p>
              <p className="text-[#1B3A5C] font-medium">{[z.rechtsgebiet, z.gegner && `gegen ${z.gegner}`].filter(Boolean).join(' ') || 'Ihre Akte'}</p>
              {z.details && <p className="whitespace-pre-line pt-2" data-testid="zustimmung-details-text">{z.details}</p>}
            </section>
            {z.status === 'offen' ? <Formular z={z} onDone={load} /> : <Ergebnis z={z} />}
          </>)}
        </div>
      </main>
    </PortalSeite>
  );
}
